import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { Button } from './ui/Button';
import { Badge } from './ui/Badge';
import { Shield, ArrowRight, Lock, Globe, Terminal } from 'lucide-react';
import { useLanguage } from '../contexts/LanguageContext';
export function Hero() {
  const {
    t
  } = useLanguage();
  const [typedText, setTypedText] = useState('');
  const fullText = t('hero.subtitle');
  useEffect(() => {
    let currentIndex = 0;
    const typingSpeed = 30;
    const typingInterval = setInterval(() => {
      if (currentIndex <= fullText.length) {
        setTypedText(fullText.slice(0, currentIndex));
        currentIndex++;
      } else {
        clearInterval(typingInterval);
      }
    }, typingSpeed);
    return () => clearInterval(typingInterval);
  }, [fullText]);
  return <section className="relative overflow-hidden" data-id="element-271">
      {/* Animated Cyber Grid */}
      <div className="absolute inset-0 bg-[linear-gradient(to_right,#0f172a_1px,transparent_1px),linear-gradient(to_bottom,#0f172a_1px,transparent_1px)] bg-[size:4rem_4rem] [mask-image:radial-gradient(ellipse_80%_50%_at_50%_0%,#000_70%,transparent_110%)] opacity-20" data-id="element-272"></div>

      {/* Cyber Glow Effects */}
      <div className="absolute top-0 left-1/4 w-96 h-96 bg-cyan-500/15 rounded-full blur-3xl animate-pulse" data-id="element-273"></div>
      <div className="absolute bottom-0 right-1/4 w-96 h-96 bg-emerald-500/8 rounded-full blur-3xl animate-pulse animation-delay-300" data-id="element-274"></div>
      <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[600px] h-[600px] bg-cyan-500/8 rounded-full blur-3xl animate-pulse animation-delay-500" data-id="element-275"></div>

      {/* Scanline Effect */}
      <div className="absolute inset-0 bg-[linear-gradient(to_bottom,transparent_50%,rgba(6,182,212,0.02)_50%)] bg-[length:100%_4px] pointer-events-none" data-id="element-276"></div>

      <div className="container-modern relative z-10" data-id="element-277">
        <div className="flex flex-col items-center text-center py-12 sm:py-16 md:py-24 lg:py-32 xl:py-40 px-4" data-id="element-278">
          {/* Terminal-Style Badge - Mobile Optimized */}
          <div className="animate-fade-in-down max-w-full" data-id="element-279">
            <Badge variant="primary" size="lg" icon={<Terminal className="h-3 w-3 flex-shrink-0" data-id="element-281" />} data-id="element-280">
              <span className="font-mono text-[10px] xs:text-xs sm:text-sm break-all" data-id="element-282">
                $ ./cybersecurity-protocol --init
              </span>
            </Badge>
          </div>

          {/* Main Heading with Cyber Glow - Fully Responsive */}
          <h1 className="mt-6 sm:mt-8 text-3xl sm:text-4xl md:text-5xl lg:text-6xl xl:text-7xl 2xl:text-8xl font-bold tracking-tight animate-fade-in-up animation-delay-100" data-id="element-283">
            <span className="block text-slate-100 mb-2" data-id="element-284">
              {t('hero.title1')}
            </span>
            <span className="block gradient-text drop-shadow-[0_0_30px_rgba(6,182,212,0.5)]" data-id="element-285">
              {t('hero.title2')}
            </span>
          </h1>

          {/* Subheading with Typing Effect - Mobile Optimized */}
          <div className="mt-4 sm:mt-6 text-sm sm:text-base md:text-lg text-slate-400 max-w-3xl min-h-[3rem] sm:min-h-[4rem] md:min-h-[3rem] px-4" data-id="element-286">
            <p className="font-mono break-words" data-id="element-287">
              <span className="text-emerald-500" data-id="element-288">{'>'}</span> {typedText}
              <span className="animate-pulse text-cyan-400" data-id="element-289">▊</span>
            </p>
          </div>

          {/* CTA Buttons - Fully Responsive */}
          <div className="mt-8 sm:mt-10 flex flex-col sm:flex-row gap-3 sm:gap-4 animate-fade-in-up animation-delay-300 w-full sm:w-auto px-4" data-id="element-290">
            <Link to="/start" className="w-full sm:w-auto" data-id="element-291">
              <Button size="xl" className="group relative overflow-hidden w-full sm:w-auto" data-id="element-292">
                <span className="relative z-10 flex items-center justify-center" data-id="element-293">
                  <span className="text-sm sm:text-base" data-id="element-294">
                    {t('hero.cta.start')}
                  </span>
                  <ArrowRight className="ml-2 h-4 w-4 sm:h-5 sm:w-5 group-hover:translate-x-1 transition-transform" data-id="element-295" />
                </span>
                <div className="absolute inset-0 bg-gradient-to-r from-cyan-600 to-cyan-400 opacity-0 group-hover:opacity-100 transition-opacity" data-id="element-296"></div>
              </Button>
            </Link>
            <Link to="/about/mission" className="w-full sm:w-auto" data-id="element-297">
              <Button variant="outline" size="xl" className="group w-full sm:w-auto" data-id="element-298">
                <Terminal className="mr-2 h-4 w-4 sm:h-5 sm:w-5 group-hover:text-cyan-400 transition-colors" data-id="element-299" />
                <span className="text-sm sm:text-base" data-id="element-300">
                  {t('hero.cta.learn')}
                </span>
              </Button>
            </Link>
          </div>

          {/* Trust Indicators - Fully Responsive Grid */}
          <div className="mt-12 sm:mt-16 grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-4 sm:gap-6 w-full max-w-4xl animate-fade-in-up animation-delay-400 px-4" data-id="element-301">
            <CyberCard icon={<Shield className="h-5 w-5 sm:h-6 sm:w-6" data-id="element-303" />} title={t('hero.trust.standards')} description={t('hero.trust.standardsDesc')} accentColor="cyan" data-id="element-302" />
            <CyberCard icon={<Lock className="h-5 w-5 sm:h-6 sm:w-6" data-id="element-305" />} title={t('hero.trust.defensive')} description={t('hero.trust.defensiveDesc')} accentColor="emerald" data-id="element-304" />
            <CyberCard icon={<Globe className="h-5 w-5 sm:h-6 sm:w-6" data-id="element-307" />} title={t('hero.trust.global')} description={t('hero.trust.globalDesc')} accentColor="cyan" data-id="element-306" />
          </div>
        </div>
      </div>

      {/* Bottom Fade */}
      <div className="absolute bottom-0 left-0 right-0 h-32 bg-gradient-to-t from-black to-transparent" data-id="element-308"></div>
    </section>;
}
interface CyberCardProps {
  icon: React.ReactNode;
  title: string;
  description: string;
  accentColor: 'cyan' | 'emerald';
}
function CyberCard({
  icon,
  title,
  description,
  accentColor
}: CyberCardProps) {
  const glowColor = accentColor === 'cyan' ? 'group-hover:shadow-[0_0_30px_rgba(6,182,212,0.3)]' : 'group-hover:shadow-[0_0_30px_rgba(16,185,129,0.3)]';
  const iconColor = accentColor === 'cyan' ? 'text-cyan-400' : 'text-emerald-400';
  const iconBgColor = accentColor === 'cyan' ? 'bg-cyan-500/10' : 'bg-emerald-500/10';
  const iconBgHover = accentColor === 'cyan' ? 'group-hover:bg-cyan-500/20' : 'group-hover:bg-emerald-500/20';
  return <div className={`glass-hover rounded-2xl p-4 sm:p-6 group relative overflow-hidden ${glowColor} transition-all duration-300`} data-id="element-309">
      {/* Cyber Border Effect */}
      <div className="absolute inset-0 rounded-2xl border border-slate-800 group-hover:border-cyan-500/30 transition-colors" data-id="element-310"></div>

      {/* Scanline Effect */}
      <div className="absolute inset-0 bg-[linear-gradient(to_bottom,transparent_50%,rgba(6,182,212,0.02)_50%)] bg-[length:100%_2px] opacity-0 group-hover:opacity-100 transition-opacity" data-id="element-311"></div>

      <div className="flex flex-col items-center text-center relative z-10" data-id="element-312">
        <div className={`h-10 w-10 sm:h-12 sm:w-12 rounded-xl ${iconBgColor} ${iconBgHover} flex items-center justify-center mb-3 sm:mb-4 ${iconColor} transition-all duration-300`} data-id="element-313">
          {icon}
        </div>
        <h3 className="text-sm sm:text-base font-bold text-slate-100 mb-1 font-mono" data-id="element-314">
          {title}
        </h3>
        <p className="text-xs sm:text-sm text-slate-400 font-sans" data-id="element-315">
          {description}
        </p>
      </div>
    </div>;
}